import { useEffect, useRef, useState } from 'react';

import { useSplit } from 'hooks';
import { FeatureFlagsService } from 'services/api/featureFlags.service';
import Toast, { TOAST_TYPES } from 'services/notifications';
import { SPLIT_CONSTANTS } from 'services/split.io';
import { isValidNetworkId } from 'utils/network';
import Switch from '../../../../widgets/switch';

const MARKETING_SECURITY_LABEL = 'Marketing Security';
const LOAD_ERROR = 'Failed to load the Marketing Security state';
const UPDATE_ERROR = 'Failed to update Marketing Security';
const UPDATE_SUCCESS = (enabled: boolean) => `Marketing Security ${enabled ? 'enabled' : 'disabled'}`;

interface MarketingSecurityToggleProps {
    networkId?: number;
    disabled?: boolean;
}

/** `isValidNetworkId` owns the rule; this only narrows it for the strict `FeatureFlagsService` signature. */
const isTargetableNetwork = (networkId?: number): networkId is number => isValidNetworkId(networkId);

/** The API layer rejects with `error.response.data`, so the reason can be a string, an object or undefined. */
const describeError = (error: unknown): string => {
    if (typeof error === 'string') {
        return error;
    }
    const message = (error as { message?: string })?.message;
    return message || JSON.stringify(error ?? 'unknown error');
};

/**
 * Targets the account's network in the Marketing Security Split flag, so enabling the product for a
 * customer no longer requires an R&D change in Split.
 */
const MarketingSecurityToggleControl = ({ networkId, disabled }: MarketingSecurityToggleProps) => {
    const [isEnabled, setIsEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        // Never keep the previous network's answer on screen while a new one loads.
        setIsEnabled(false);

        if (!isTargetableNetwork(networkId)) {
            return;
        }

        let isCurrent = true;
        setIsLoading(true);

        FeatureFlagsService.getStatus(SPLIT_CONSTANTS.PARADOME_MARKETING_SECURITY, networkId)
            .then((status) => {
                if (isCurrent) {
                    setIsEnabled(status);
                }
            })
            .catch((error) => {
                if (isCurrent) {
                    console.error(LOAD_ERROR, { networkId, reason: describeError(error) });
                    Toast({ message: LOAD_ERROR, type: TOAST_TYPES.ERROR });
                }
            })
            .finally(() => {
                if (isCurrent) {
                    setIsLoading(false);
                }
            });

        return () => {
            isCurrent = false;
        };
    }, [networkId]);

    const onToggle = async (status: boolean) => {
        if (!isTargetableNetwork(networkId)) {
            return;
        }

        const targetNetworkId = networkId;
        setIsLoading(true);

        try {
            await FeatureFlagsService.update(SPLIT_CONSTANTS.PARADOME_MARKETING_SECURITY, targetNetworkId, status);
            // Ignore a result that came back after unmount, or after the account changed under us.
            if (isMounted.current && targetNetworkId === networkId) {
                setIsEnabled(status);
                Toast({ message: UPDATE_SUCCESS(status), type: TOAST_TYPES.SUCCESS });
            }
        } catch (error) {
            console.error(UPDATE_ERROR, { networkId: targetNetworkId, reason: describeError(error) });
            if (isMounted.current) {
                Toast({ message: UPDATE_ERROR, type: TOAST_TYPES.ERROR });
            }
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    };

    return (
        <div className="field-group">
            <Switch
                type="checkbox"
                className="control-group-toggle"
                checked={isEnabled}
                isLoading={isLoading}
                disabled={disabled || isLoading || !isTargetableNetwork(networkId)}
                onChange={onToggle}
                inputProps={{ 'aria-label': MARKETING_SECURITY_LABEL }}
            />
            <div className="field-key line">{MARKETING_SECURITY_LABEL}</div>
        </div>
    );
};

/**
 * Wrapper flag around the control above: the toggle only exists once
 * `PARADOME_MARKETING_SECURITY_WRAPPER_FLAG` is on, so the rollout of the per-network toggle can be
 * stopped without a deploy. Nothing is fetched while the flag is off.
 */
export const MarketingSecurityToggle = (props: MarketingSecurityToggleProps) => {
    const { isOn: isWrapperFlagOn } = useSplit(SPLIT_CONSTANTS.PARADOME_MARKETING_SECURITY_WRAPPER_FLAG);

    if (!isWrapperFlagOn) {
        return null;
    }

    return <MarketingSecurityToggleControl {...props} />;
};
